import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { endpoints } from '../../shared/apis/endpoints';
import { Driver } from '../../catalogs/models/drivers';
import { BaseGetAll } from '../../shared/commons/generic.responses';
import { CatalogService } from '../../catalogs/catalog.service';
import { DriverCatalogReq } from '../../catalogs/pages/drivers/drivers.component';
import { AgingSummaryData, AgingInvoice, AgingSummaryCustomer } from '../models/aging-summary';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  constructor(
    private http: HttpClient,
    private catalogService: CatalogService
  ) { }

  /**
   * Get all drivers for dropdown selection
   * @returns Observable<BaseGetAll<Driver[]>>
   */
  getAllDrivers(): Observable<BaseGetAll<Driver[]>> {
    const driverReq = new DriverCatalogReq();
    driverReq.IsActive = true;
    return this.catalogService.GetAllDrivers(driverReq);
  }

  /**
   * Generate Driver Daily Log Report
   * @param driverId - The driver ID
   * @param date - The date for the report (YYYY-MM-DD format)
   * @returns Observable<Blob> - PDF file as blob
   */
  generateDriverDailyLogReport(driverId: string, date: string): Observable<Blob> {
    const url =  `${endpoints.GET_DRIVER_DAILY_LOG}/${driverId}/${date}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Get Aging Summary Data (for interactive view)
   * @returns Observable<BaseGetAll<AgingSummaryCustomer[]>> - Data for display
   */
  getAgingSummaryData(): Observable<BaseGetAll<AgingSummaryCustomer[]>> {
    return this.http.get<BaseGetAll<AgingSummaryCustomer[]>>(endpoints.GET_AGING_SUMMARY_DATA);
  }

  /**
   * Get invoice details for a specific customer
   * @param customerId - Customer ID
   * @returns Observable<BaseGetAll<AgingInvoice[]>> - Invoice details
   */
  getCustomerAgingInvoices(customerId: number): Observable<BaseGetAll<AgingInvoice[]>> {
    const url = `${endpoints.GET_AGING_SUMMARY}/customer/${customerId}/invoices`;
    return this.http.get<BaseGetAll<AgingInvoice[]>>(url);
  }

  /**
   * Generate Aging Summary Report PDF (download)
   * @returns Observable<Blob> - PDF file as blob
   */
  generateAgingSummaryReportPDF(): Observable<Blob> {
    return this.http.get(endpoints.GET_AGING_SUMMARY, { responseType: 'blob' });
  }

  /**
   * Generate Detailed Aging Summary Report PDF (grouped by customer and order)
   * @returns Observable<Blob> - PDF file as blob
   */
  generateAgingSummaryDetailedPDF(): Observable<Blob> {
    return this.http.get(endpoints.GET_AGING_SUMMARY_DETAILED, { responseType: 'blob' });
  }

  /**
   * Get Movement Detail Report Data
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Observable<BaseGetAll<any[]>> - Movement detail data
   */
  getMovementDetail(startDate: string, endDate: string): Observable<BaseGetAll<any[]>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<BaseGetAll<any[]>>(endpoints.GET_MOVEMENT_DETAIL, { params });
  }
}
